import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { pageWindow } from '@/lib/pagination'
import { cn } from '@/lib/utils'

interface Props {
  page: number
  pageCount: number
  /** The first and last row on this page, one-based and inclusive. */
  from: number
  to: number
  total: number
  /**
   * What the rows are — "events", "people". Read into the controls' accessible
   * names, so "Next page" in a screen reader says which list it pages.
   */
  unit: string
  onPage: (page: number) => void
  className?: string | undefined
}

/** A page number, or the gap standing in for the pages between. */
function PageButton({
  page,
  current,
  onPage,
}: {
  page: number
  current: boolean
  onPage: (page: number) => void
}): React.ReactElement {
  return (
    <Button
      variant="ghost"
      size="sm"
      // Styled from `aria-current` rather than a prop-driven class, the way the
      // design system does it, so the state a screen reader announces and the
      // state you can see cannot drift apart.
      aria-current={current ? 'page' : undefined}
      aria-label={`Page ${String(page)}`}
      onClick={() => onPage(page)}
      className={cn(
        'h-[30px] min-w-[30px] justify-center px-2 font-mono text-small tabular-nums',
        // The wash alone is a pale azure that works across the width of a
        // selected row and all but disappears in a 2rem box — which page you are
        // on is the one thing this control has to say. `text-accent-ink` is the
        // accent as *text*, not `text-primary`, which is the color a filled
        // button is painted.
        current
          ? 'bg-surface-selected font-semibold text-accent-ink hover:bg-surface-selected-hover hover:text-accent-ink'
          : undefined,
      )}
    >
      {page}
    </Button>
  )
}

/**
 * The bar under a long list: how much of it you are looking at, and the way
 * through the rest.
 *
 * Renders **nothing** when there is one page. The count is already in the
 * table's header band, so a bar there would repeat it around a control that
 * cannot do anything — and it means every list in the app can carry this
 * without a short list growing furniture.
 *
 * Mirrors `.ds-pagination` in the design system; `table.parity.spec.ts`
 * compares the two.
 */
export function Pagination({
  page,
  pageCount,
  from,
  to,
  total,
  unit,
  onPage,
  className,
}: Props): React.ReactElement | null {
  if (pageCount <= 1) return null

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3',
        className,
      )}
    >
      {/* `tabular-nums` so the readout does not shift as the digits change under
          it, which on a fast pager is most of what the eye sees. */}
      <span className="font-mono text-small tabular-nums text-ink-muted">
        {from}–{to} of {total} {unit}
      </span>
      <nav aria-label={`${unit} pages`} className="ml-auto flex items-center gap-1">
        {/* Disabled rather than hidden at the ends, so the strip keeps its shape
            and its position instead of shifting as you arrive at one. */}
        <Button
          variant="ghost"
          size="sm"
          className="h-[30px] min-w-[30px] justify-center px-2"
          disabled={page <= 1}
          aria-label={`Previous page of ${unit}`}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pageWindow(page, pageCount).map((slot) =>
          slot.kind === 'page' ? (
            <PageButton key={slot.page} page={slot.page} current={slot.page === page} onPage={onPage} />
          ) : (
            // A span, not a disabled button: there is nothing to press, and a
            // disabled control here would be one that never does anything.
            <span
              key={`gap-${String(slot.after)}`}
              aria-hidden="true"
              className="inline-flex min-w-[22px] select-none items-center justify-center text-ink-muted"
            >
              …
            </span>
          ),
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-[30px] min-w-[30px] justify-center px-2"
          disabled={page >= pageCount}
          aria-label={`Next page of ${unit}`}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </nav>
    </div>
  )
}
